import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import Header from '../../components/ui/Header';
import Icon from '../../components/AppIcon';
import Button from '../../components/ui/Button';
import api from '../../services/api';
import { useAuth } from '../../contexts/AuthContext';

const GiveawayDetailPage = () => {
  const { giveawayId } = useParams();
  const { isAuthenticated, user } = useAuth();
  const [giveaway, setGiveaway] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [entering, setEntering] = useState(false);
  const [hasEntered, setHasEntered] = useState(false);

  useEffect(() => {
    const fetchGiveaway = async () => {
      try {
        setLoading(true);
        setError(null);

        const response = await api.get(`/giveaways/${giveawayId}/`);
        setGiveaway(response.data);
        
        // Check if user has already entered
        if (isAuthenticated) {
          try {
            const entryResponse = await api.get(`/giveaways/${giveawayId}/entry-status/`);
            setHasEntered(entryResponse.data.has_entered);
          } catch (entryError) {
            console.warn('Could not check entry status:', entryError);
          }
        }
        
      } catch (err) {
        console.error('Error fetching giveaway:', err);
        setError(err.response?.data?.message || err.message || 'Failed to load giveaway');
      } finally {
        setLoading(false);
      }
    };

    if (giveawayId) {
      fetchGiveaway();
    }
  }, [giveawayId, isAuthenticated]);

  const handleEnterGiveaway = async () => {
    if (!isAuthenticated) {
      // Redirect to login
      window.location.href = '/login';
      return;
    }

    try {
      setEntering(true);
      await api.post(`/giveaways/${giveawayId}/enter/`);
      setHasEntered(true);
    } catch (err) {
      console.error('Error entering giveaway:', err);
      alert(err.response?.data?.message || 'Failed to enter giveaway');
    } finally {
      setEntering(false);
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getTimeRemaining = (endDate) => {
    const now = new Date();
    const end = new Date(endDate);
    const diff = end - now;
    
    if (diff <= 0) return 'Ended';
    
    const days = Math.floor(diff / (1000 * 60 * 60 * 24));
    const hours = Math.floor((diff % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
    
    if (days > 0) return `${days} days, ${hours} hours left`;
    return `${hours} hours left`;
  };

  // Loading state
  if (loading) {
    return (
      <div className="min-h-screen bg-background">
        <Header />
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-24">
          <div className="flex items-center justify-center py-20">
            <div className="text-center">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto mb-4"></div>
              <p className="text-accent-600">Loading giveaway...</p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Error state
  if (error) {
    return (
      <div className="min-h-screen bg-background">
        <Header />
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-24">
          <div className="flex items-center justify-center py-20">
            <div className="text-center">
              <Icon name="AlertCircle" size={48} className="text-error mx-auto mb-4" />
              <h2 className="text-2xl font-bold text-accent-800 mb-2">Giveaway Not Found</h2>
              <p className="text-accent-600 mb-6">{error}</p>
              <Button
                variant="primary"
                onClick={() => window.history.back()}
                iconName="ArrowLeft"
                iconPosition="left"
              >
                Go Back
              </Button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // No giveaway data
  if (!giveaway) {
    return (
      <div className="min-h-screen bg-background">
        <Header />
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-24">
          <div className="flex items-center justify-center py-20">
            <div className="text-center">
              <Icon name="Gift" size={48} className="text-accent-400 mx-auto mb-4" />
              <h2 className="text-2xl font-bold text-accent-800 mb-2">No Giveaway Found</h2>
              <p className="text-accent-600">Unable to load giveaway information.</p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  const isActive = giveaway.status === 'ACTIVE';
  const hasEnded = new Date(giveaway.end_date) < new Date();

  return (
    <div className="min-h-screen bg-background">
      <Helmet>
        <title>{giveaway.title} - Giveaway | Giftsy</title>
        <meta name="description" content={giveaway.description || `Enter to win ${giveaway.title} on Giftsy`} />
        
        {/* Open Graph Tags */}
        <meta property="og:title" content={`${giveaway.title} - Giveaway | Giftsy`} />
        <meta property="og:description" content={giveaway.description || `Enter to win ${giveaway.title} on Giftsy`} />
        {giveaway.image && <meta property="og:image" content={giveaway.image} />}
        <meta property="og:url" content={window.location.href} />
        <meta property="og:type" content="article" />
        <meta property="og:site_name" content="Giftsy" />
        
        {/* Twitter Card Tags */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={`${giveaway.title} - Giveaway | Giftsy`} />
        <meta name="twitter:description" content={giveaway.description || `Enter to win ${giveaway.title} on Giftsy`} />
        {giveaway.image && <meta name="twitter:image" content={giveaway.image} />}
      </Helmet>

      <Header />
      
      <main className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 pt-24 pb-16">
        {/* Hero Section */}
        <div className="text-center mb-8">
          <div className="inline-flex items-center justify-center w-16 h-16 bg-gradient-to-br from-primary to-secondary rounded-full mb-4">
            <Icon name="Gift" size={32} className="text-white" />
          </div>
          <h1 className="text-4xl font-bold text-accent-800 mb-4">
            {giveaway.title}
          </h1>
          {giveaway.description && (
            <p className="text-lg text-accent-600 max-w-2xl mx-auto mb-6">
              {giveaway.description}
            </p>
          )}
          
          {/* Status Badge */}
          <div className="inline-flex items-center space-x-2 mb-4">
            {isActive && !hasEnded ? (
              <div className="bg-success-100 text-success-800 px-3 py-1 rounded-full text-sm font-medium">
                <Icon name="Clock" size={14} className="inline mr-1" />
                {getTimeRemaining(giveaway.end_date)}
              </div>
            ) : (
              <div className="bg-error-100 text-error-800 px-3 py-1 rounded-full text-sm font-medium">
                <Icon name="X" size={14} className="inline mr-1" />
                Ended
              </div>
            )}
          </div>
        </div>

        {/* Giveaway Image */}
        {giveaway.image && (
          <div className="mb-8">
            <img
              src={giveaway.image}
              alt={giveaway.title}
              className="w-full h-64 md:h-96 object-cover rounded-2xl shadow-lg"
            />
          </div>
        )}

        {/* Giveaway Details */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mb-8">
          {/* Prize Information */}
          <div className="bg-white rounded-2xl border border-accent-100 p-6">
            <h3 className="text-xl font-semibold text-accent-800 mb-4 flex items-center">
              <Icon name="Award" size={20} className="mr-2 text-primary" />
              Prize Details
            </h3>
            <div className="space-y-3">
              {giveaway.prize_value && (
                <div className="flex justify-between">
                  <span className="text-accent-600">Value:</span>
                  <span className="font-medium text-accent-800">${giveaway.prize_value}</span>
                </div>
              )}
              <div className="flex justify-between">
                <span className="text-accent-600">Winners:</span>
                <span className="font-medium text-accent-800">{giveaway.max_winners || 1}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-accent-600">Entries:</span>
                <span className="font-medium text-accent-800">{giveaway.entry_count || 0}</span>
              </div>
            </div>
          </div>

          {/* Timeline */}
          <div className="bg-white rounded-2xl border border-accent-100 p-6">
            <h3 className="text-xl font-semibold text-accent-800 mb-4 flex items-center">
              <Icon name="Calendar" size={20} className="mr-2 text-primary" />
              Timeline
            </h3>
            <div className="space-y-3">
              <div>
                <span className="text-accent-600 block">Starts:</span>
                <span className="font-medium text-accent-800">{formatDate(giveaway.start_date)}</span>
              </div>
              <div>
                <span className="text-accent-600 block">Ends:</span>
                <span className="font-medium text-accent-800">{formatDate(giveaway.end_date)}</span>
              </div>
            </div>
          </div>
        </div>

        {/* Rules & Description */}
        {giveaway.rules && (
          <div className="bg-white rounded-2xl border border-accent-100 p-6 mb-8">
            <h3 className="text-xl font-semibold text-accent-800 mb-4 flex items-center">
              <Icon name="FileText" size={20} className="mr-2 text-primary" />
              Rules & Requirements
            </h3>
            <div className="prose prose-sm max-w-none">
              <p className="text-accent-700">{giveaway.rules}</p>
            </div>
          </div>
        )}

        {/* Entry Section */}
        <div className="text-center">
          {!isAuthenticated ? (
            <div className="bg-accent-50 rounded-2xl p-8">
              <Icon name="Lock" size={48} className="text-accent-400 mx-auto mb-4" />
              <h3 className="text-xl font-semibold text-accent-800 mb-2">Login Required</h3>
              <p className="text-accent-600 mb-6">You need to be logged in to enter this giveaway</p>
              <Button
                variant="primary"
                size="lg"
                iconName="LogIn"
                iconPosition="left"
                onClick={() => window.location.href = '/login'}
              >
                Login to Enter
              </Button>
            </div>
          ) : hasEnded ? (
            <div className="bg-error-50 rounded-2xl p-8">
              <Icon name="Clock" size={48} className="text-error-400 mx-auto mb-4" />
              <h3 className="text-xl font-semibold text-error-800 mb-2">Giveaway Ended</h3>
              <p className="text-error-600">This giveaway has ended and is no longer accepting entries</p>
            </div>
          ) : hasEntered ? (
            <div className="bg-success-50 rounded-2xl p-8">
              <Icon name="CheckCircle" size={48} className="text-success-400 mx-auto mb-4" />
              <h3 className="text-xl font-semibold text-success-800 mb-2">You're Entered!</h3>
              <p className="text-success-600 mb-6">Good luck! Winners will be announced after the giveaway ends.</p>
              <Button
                variant="outline"
                iconName="Share2"
                iconPosition="left"
                onClick={() => console.log('Share giveaway')}
              >
                Share with Friends
              </Button>
            </div>
          ) : (
            <div className="bg-primary-50 rounded-2xl p-8">
              <Icon name="Gift" size={48} className="text-primary mx-auto mb-4" />
              <h3 className="text-xl font-semibold text-accent-800 mb-2">Ready to Enter?</h3>
              <p className="text-accent-600 mb-6">Click below to enter this giveaway. Good luck!</p>
              <Button
                variant="primary"
                size="lg"
                iconName="Zap"
                iconPosition="left"
                onClick={handleEnterGiveaway}
                disabled={entering}
                className="shadow-lg hover:shadow-xl transition-all duration-300"
              >
                {entering ? 'Entering...' : 'Enter Giveaway'}
              </Button>
            </div>
          )}
        </div>
      </main>
    </div>
  );
};

export default GiveawayDetailPage;